import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ENTITLEMENT_LABELS } from '../entitlements/entitlement-keys';
import {
  DemoEnvironmentService,
  DemoPersona,
} from '../Services/demo-environment.service';
import { PlanTierCard } from '../user-plans/plan-tier.types';

interface DemoPlanTier extends PlanTierCard {
  screencast_limit: string;
  screenshot_interval: string;
  retention: string;
  feature_keys: string[];
}

const DEMO_PLAN_TIERS: DemoPlanTier[] = [
  {
    plan_code: 'basic',
    name: 'Basic',
    tagline: 'Employee-controlled monitoring essentials',
    highlights: [
      'App & Website Usage reports',
      'Employee-controlled mode',
      '1 work shift',
      'Screenshots from 10 minutes',
    ],
    screencast_limit: '45 min/day',
    screenshot_interval: '15 min minimum',
    retention: '90 days logs · 15 days screenshots',
    feature_keys: [
      'employee_controlled_mode',
      'website_usage',
      'live_screencast',
      'productivity_trends_preview',
      'work_replay_preview',
      'data_management_preview',
    ],
  },
  {
    plan_code: 'professional',
    name: 'Professional',
    tagline: 'Operational visibility for growing teams',
    recommended: true,
    highlights: [
      'Everything in Basic',
      'Productivity Trends & Live wall',
      'Work Replay & bulk settings',
      'Strict / auto tracking, 1 min screenshots',
    ],
    screencast_limit: '90 min/day',
    screenshot_interval: '10 min minimum',
    retention: '90 days logs · 30 days screenshots',
    feature_keys: [
      'employee_controlled_mode',
      'live_screencast',
      'live_wall',
      'productivity_trends',
      'reports_advanced_filters',
      'website_usage',
      'work_replay',
      'data_management',
      'device_management',
      'bulk_settings_update',
      'advanced_tracking_controls',
      'short_screenshot_interval',
      'manager_roles',
      'multiple_shifts',
      'period_timesheet',
    ],
  },
  {
    plan_code: 'business',
    name: 'Business',
    tagline: 'Compliance, retention, and executive reporting',
    highlights: [
      'Everything in Professional',
      'Extended data retention',
      'Executive reports',
      'Priority onboarding support',
    ],
    screencast_limit: '180 min/day',
    screenshot_interval: '5 min minimum',
    retention: '180 days logs · 60 days screenshots',
    feature_keys: [
      'employee_controlled_mode',
      'live_screencast',
      'live_wall',
      'productivity_trends',
      'reports_advanced_filters',
      'website_usage',
      'work_replay',
      'data_management',
      'device_management',
      'bulk_settings_update',
      'advanced_tracking_controls',
      'short_screenshot_interval',
      'manager_roles',
      'multiple_shifts',
      'period_timesheet',
      'extended_retention',
      'executive_reports',
    ],
  },
];

@Component({
  selector: 'app-demo-console',
  templateUrl: './demo-console.component.html',
  styleUrls: ['./demo-console.component.css'],
  standalone: false,
})
export class DemoConsoleComponent implements OnInit, OnDestroy {
  personas: DemoPersona[] = [];
  activePersona: DemoPersona | null = null;
  switching = false;
  switchError = '';
  planTiers = DEMO_PLAN_TIERS;
  selectedPlanCode = 'professional';
  private switchSub?: Subscription;

  get selectedPlan(): DemoPlanTier {
    return this.planTiers.find((t) => t.plan_code === this.selectedPlanCode) ?? this.planTiers[1];
  }

  constructor(
    public demoEnv: DemoEnvironmentService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (!this.demoEnv.isDemoRealm()) {
      this.router.navigate(['/dashboard']);
      return;
    }
    this.personas = this.demoEnv.personas;
    this.activePersona = this.demoEnv.getActivePersonaFromSession();
  }

  ngOnDestroy(): void {
    this.switchSub?.unsubscribe();
  }

  isActive(persona: DemoPersona): boolean {
    return this.activePersona?.id === persona.id;
  }

  selectPlan(planCode: string): void {
    this.selectedPlanCode = planCode;
  }

  featureLabel(key: string): string {
    return ENTITLEMENT_LABELS[key] ?? key.replace(/_/g, ' ');
  }

  exclusiveFeatures(plan: DemoPlanTier): string[] {
    const order = ['basic', 'professional', 'business'];
    const idx = order.indexOf(plan.plan_code);
    if (idx <= 0) {
      return [];
    }
    const lowerKeys = new Set(this.planTiers[idx - 1].feature_keys);
    return plan.feature_keys.filter((key) => !lowerKeys.has(key));
  }

  selectPersona(persona: DemoPersona): void {
    if (!persona.importedLocally || this.switching) {
      return;
    }
    if (this.isActive(persona)) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.switching = true;
    this.switchError = '';
    this.switchSub?.unsubscribe();
    this.switchSub = this.demoEnv.switchToPersona(persona.id).subscribe({
      error: (err) => {
        this.switching = false;
        this.switchError = err?.error?.message || err?.message || 'Failed to switch company.';
      },
    });
  }
}
